import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { ArrowDown, ArrowUp, ChevronDown, ChevronUp, ClipboardPaste, Copy, Filter, Paperclip, Trash2, X } from 'lucide-react'
import { PROJECT_WORK_STEP_STATUSES_RELEASED, erfuelltRecht } from '@symphenia/shared-types'
import { KlappUmschalter, useKlappModus } from '@/components/klappmodus'
import { FilterBar } from '@/components/list/FilterBar'
import type { ActiveFilter, FilterFieldDef } from '@/components/list/list.types'
import { ZiehBereich, useRandMarkieren, useZeilenZiehen } from '@/components/ziehen/ZeilenZiehen'
import { verschiebeMehrereVor } from '@/components/ziehen/ziehen'
import { apiErrorText } from '@/lib/api-error'
import { api } from '@/lib/axios'
import { useAuthStore } from '@/lib/auth.store'
import { mitStand, useFeld, useGruppe, useSpeicherstelle, type Datensatz, type Speicherstelle } from '@/lib/speichern'
import { fetchQualifications } from '../api/projects-head.api'
import {
  changeWorkStepStatus,
  createWorkStep,
  deleteWorkStep,
  fetchWorkStepTrash,
  fetchWorkSteps,
  pasteWorkSteps,
  releaseWorkSteps,
  reorderWorkSteps,
  restoreWorkStep,
  revokeWorkStepRelease,
  uploadFile,
} from '../api/projects-phase-d.api'
import { WORK_STEP_STATUS_LABELS, alsStunden, alsTag } from '../project-labels'
import type { PositionDetail, WorkStepRow, WorkStepsResponse } from '../types/phase-d.types'
import type { ProjectDetail, ProjectQualification } from '../types/project.types'
import { Feld, RasterFeld, ZeilenBlock, ZeilenListe, leseZahl, useListenBreite, useSeiteDaneben, zelle, type Seite, type Spalte } from './zeilenblock'
import { Aufklapper } from './PositionBomTab'
import { PapierkorbFenster, RueckgaengigHinweis } from './Papierkorb'
import { ProjectFilesPanel } from './ProjectFilesPanel'
import { WorkOrderButton } from './WorkOrderButton'
import { OHNE, SCHRITT_FREIGEGEBEN, beobachteAblage, filterAnzahl, leseAblage, schreibeAblage, schrittPasst, type SchrittAblage } from './arbeitsschritte-werkzeuge'
import { frage } from '@/lib/dialoge'

const FREIGABE_CODES: Record<string, string> = {
  WORKSTEP_RELEASE_REQUIRES_POSITION: 'Position ist nicht freigegeben',
  WORKSTEP_STATUS_TRANSITION: 'Status lässt die Freigabe nicht zu',
}

/** Block rechts: Status, Freigaben, Dateien — wie in der Stückliste (Nachtrag 5a). */
const SEITE: Seite = { breite: 'w-[10.5rem]', min: 168, titel: 'Status · Freigaben · Dateien' }

/**
 * Nachtrag 15 (N15-18): Ab dieser **Listenbreite** sind Kategorie und Qualifikation Spalten der ersten Zeile,
 * und der Block rechts ist doppelt so breit und zweispaltig — die Zeile wird um gut ein Drittel niedriger.
 */
const BREIT_AB = 1522
const SEITE_BREIT: Seite = { breite: 'w-[21rem]', min: 336, titel: 'Status · Freigaben · Dateien' }

const knopf = 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted'
const klein = 'inline-flex items-center gap-1 rounded border border-border px-2 py-1 hover:bg-muted'

/**
 * Wie `zelle`, aber ein Feld, das als ungültig gemeldet ist, bleibt rot stehen.
 * Der Speicherbaustein setzt dafür `aria-invalid` — die leere Zeile setzt es
 * selbst, damit beide Zeilenarten gleich aussehen.
 */
const eingabe = `${zelle} aria-[invalid=true]:border-destructive`

/** Filterleiste (Nachtrag 10) — je Benutzer gemerkt wie die der Stückliste. */
const FILTER_LAYOUT = 'project-work-steps'
const FILTER_FELDER = ['category', 'dueUntil', 'status', 'released', 'qualification']
const FILTER_OFFEN = 'symphenia-work-steps-filter-offen'
const MEHRFACH = ['is', 'is_not'] as FilterFieldDef['operators']

/**
 * AP-35 Detailblatt 4 8. — **Der Reiter Arbeitsschritte an der Position.**
 *
 * Anordnung nach Nachtrag 9 (Auftraggeber 19.9.2026, am Screenshot abgestimmt),
 * dasselbe Muster wie die Stückliste: Zeile 1 trägt Nr., Soll, Bezeichnung,
 * Mengenbezug, „Fertigstellung bis“ und die Fixierung; darunter stehen
 * spaltengenau Beschreibung, Kategorie, Qualifikation, „Fertiggestellt am“ und
 * die Notiz zur Fixierung. Rechts steht über die ganze Höhe der Block mit
 * Status, Ist-Stunden, Freigabe, „wartet auf“ und Dateien. **Nichts klappt zu**
 * — jeder Schritt zeigt immer alle Felder.
 *
 * - Die **Kategorie** ist der Eintrag aus der Phasenliste der Einstellungen
 *   (`phaseId`) — dieselben Phasen wie im Arbeits-Cockpit.
 * - **„Fertiggestellt am“ eintragen meldet fertig**, leeren heißt Nacharbeit
 *   (mit Begründung).
 * - Freigegeben wird **je Schritt** über das Kästchen im Block rechts.
 * - Die **Nummer ist der Scan-Code** des Auftrags und steht als erste
 *   Spalte — unveränderlich.
 *
 * Nachtrag 10 (Auftraggeber 19.9.2026): Werkzeuge wie in der Stückliste —
 * am linken Rand markieren, an der freien Fläche ziehen (markierte wandern
 * gemeinsam), kopieren und einfügen (auch in eine andere Position), eine
 * ausklappbare Filterleiste, Löschen mit Rückgängig und Papierkorb. Die
 * Reihenfolge ist die gezogene, nicht mehr die der Kategorien. „Vorlage
 * einfügen“ und der Sammelknopf „Aufträge“ entfallen; gedruckt wird je Schritt.
 *
 * **Seit AP-36 Phase F speichert jede Zeile über `lib/speichern`:** Ein Schritt
 * ist eine Speicherstelle (`workstep:<id>`), die Zeile ihre Gruppe — die Felder
 * gehen beim Verlassen der Zeile gemeinsam hinaus, Auswahlen sofort, Esc stellt
 * sie wieder her (`.claude/rules/speichern.md`). Was eine Handlung ist —
 * freigeben, Status wechseln, fertig melden, Auftrag, löschen — bleibt eine.
 *
 * Nachtrag 15 (N15-18, 11.10.2026): Ab 1.522 px **eigener** Breite stehen
 * Kategorie und Qualifikation als Spalten in Zeile 1 (nach dem Mengenbezug);
 * Beschreibung und Notiz zur Fixierung sind zwei Zeilen hoch, der Block rechts
 * ist zweispaltig. Die Tab-Reihenfolge bleibt die aus Nachtrag 10. Darunter
 * gilt unverändert die Anordnung aus Nachtrag 9.
 */
export function PositionWorkStepsTab({
  projekt,
  position,
  onChanged,
}: {
  projekt: ProjectDetail
  position: PositionDetail
  onChanged: () => Promise<void> | void
}) {
  const { user } = useAuthStore()
  const darf = (recht: string) => erfuelltRecht(recht, user?.permissions)
  const darfPflegen = darf('projects.workstep.manage')
  const darfFreigeben = darf('projects.approve.execution')
  const darfDateien = darf('projects.position.manage')
  // Nachtrag 9: der Auftrag (Export-Recht) und das Senden in die PWA (Einteilung).
  const darfAuftrag = darf('projects.export.run')
  const darfSenden = darf('projects.assign.manage')

  const [daten, setDaten] = useState<WorkStepsResponse | null>(null)
  // Auftraggeber 10.10.2026: Zeilen immer aufgeklappt, immer zugeklappt oder beim Bearbeiten — je Benutzer gemerkt.
  // Die Vorgabe bleibt „immer aufgeklappt“: So stand die Liste seit Nachtrag 9.
  const [klappModus, setKlappModus] = useKlappModus('project_worksteps', 'auf')
  const [qualifikationen, setQualifikationen] = useState<ProjectQualification[]>([])
  const [fehler, setFehler] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [neuSchluessel, setNeuSchluessel] = useState(0)
  const [neuFokus, setNeuFokus] = useState(false)
  const [markiert, setMarkiert] = useState<Set<string>>(new Set())
  const [filterWerte, setFilterWerte] = useState<Record<string, ActiveFilter>>({})
  const [filterOffen, setFilterOffen] = useState(() => {
    try {
      return localStorage.getItem(FILTER_OFFEN) === '1'
    } catch {
      return false
    }
  })
  const schalteFilter = (offen: boolean) => {
    setFilterOffen(offen)
    try {
      localStorage.setItem(FILTER_OFFEN, offen ? '1' : '0')
    } catch {
      /* nur Bequemlichkeit */
    }
  }
  /** Zuletzt gelöscht — „Rückgängig“ holt den jüngsten zurück. */
  const [geloescht, setGeloescht] = useState<{ trashId: string; name: string }[]>([])
  const [papierkorbOffen, setPapierkorbOffen] = useState(false)
  const [ablage, setAblage] = useState<SchrittAblage | null>(() => leseAblage(user?.id))
  const behaelter = useRef<HTMLDivElement>(null)
  // Nachtrag 15 (N15-18): Die Liste richtet sich nach ihrer eigenen Breite, nicht nach dem Fenster. Gemessen
  // wird am äußersten Element — es steht schon da, bevor die Schritte geladen sind.
  const { ref: listenRef, breite } = useListenBreite()
  const breit = breite >= BREIT_AB

  useEffect(() => beobachteAblage(() => setAblage(leseAblage(user?.id))), [user?.id])

  const laden = useCallback(async () => {
    try {
      setDaten(await fetchWorkSteps(position.id))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }, [position.id])

  useEffect(() => {
    void laden()
    setGeloescht([])
    setMarkiert(new Set())
    fetchQualifications()
      .then((q) => setQualifikationen(q.filter((x) => x.isActive)))
      .catch(() => undefined)
  }, [laden])

  async function fuehreAus(aktion: () => Promise<unknown>, erfolg?: string) {
    setFehler(null)
    setHinweis(null)
    try {
      await aktion()
      if (erfolg) setHinweis(erfolg)
      await laden()
      await onChanged()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  async function freigeben(ids: string[]) {
    setFehler(null)
    setHinweis(null)
    try {
      const ergebnis = await releaseWorkSteps(ids)
      if (ergebnis.skipped.length) {
        setFehler(`Nicht freigegeben${ids.length > 1 ? ` (${ergebnis.skipped.length})` : ''}: ${FREIGABE_CODES[ergebnis.skipped[0].code] ?? ergebnis.skipped[0].code}`)
      }
      await laden()
      await onChanged()
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  async function dateienAnhaengen(s: WorkStepRow, dateien: File[]) {
    setFehler(null)
    setHinweis(null)
    let ok = 0
    const fehlgeschlagen: string[] = []
    for (const d of dateien) {
      try {
        await uploadFile(projekt.id, d, { entityType: 'WORK_STEP', entityId: s.id })
        ok++
      } catch (e) {
        fehlgeschlagen.push(`${d.name}: ${apiErrorText(e, 'nicht hochgeladen')}`)
      }
    }
    if (ok) setHinweis(`${ok} ${ok === 1 ? 'Datei' : 'Dateien'} an „${s.activityName}“ gehängt.`)
    if (fehlgeschlagen.length) setFehler(fehlgeschlagen.join(' · '))
    await laden()
  }

  // ── Löschen, Rückgängig, Papierkorb ─────────────────────────────────────

  /** Löschen ohne Rückfrage — es lässt sich zurücknehmen (Papierkorb, 30 Tage). */
  async function loeschen(ids: string[]) {
    setFehler(null)
    setHinweis(null)
    const weg: { trashId: string; name: string }[] = []
    const fehlgeschlagen: string[] = []
    for (const id of ids) {
      try {
        weg.push(await deleteWorkStep(id))
      } catch (e) {
        const s = daten?.steps.find((x) => x.id === id)
        fehlgeschlagen.push(`${s ? `${s.number} ${s.activityName}` : id}: ${apiErrorText(e, 'nicht gelöscht')}`)
      }
    }
    if (weg.length) setGeloescht((g) => [...g, ...weg])
    if (fehlgeschlagen.length) setFehler(fehlgeschlagen.join(' · '))
    setMarkiert((m) => new Set([...m].filter((id) => !ids.includes(id))))
    await laden()
    await onChanged()
  }

  async function wiederherstellen(trashId: string) {
    setFehler(null)
    setHinweis(null)
    try {
      setDaten(await restoreWorkStep(trashId))
      setGeloescht((g) => g.filter((x) => x.trashId !== trashId))
      await onChanged()
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Schritt ließ sich nicht wiederherstellen.'))
    }
  }

  // ── Reihenfolge ─────────────────────────────────────────────────────────

  /** Neue Reihenfolge an den Server; bis zur Antwort steht sie schon so da. */
  async function ordne(ids: readonly string[]) {
    if (!daten) return
    const nach = new Map(daten.steps.map((s) => [s.id, s]))
    setDaten({ ...daten, steps: ids.map((id) => nach.get(id)!).filter(Boolean) })
    try {
      setDaten(await reorderWorkSteps(position.id, ids.map((id, i) => ({ id, sortOrder: (i + 1) * 10 }))))
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Reihenfolge ließ sich nicht speichern.'))
      await laden()
    }
  }

  const alle = useMemo(() => daten?.steps ?? [], [daten])
  const zeilen = useMemo(() => alle.filter((s) => schrittPasst(s, filterWerte)), [alle, filterWerte])
  const gefiltert = filterAnzahl(filterWerte) > 0
  const auswahl = alle.filter((s) => markiert.has(s.id)).map((s) => s.id)

  async function schiebe(richtung: -1 | 1) {
    if (!auswahl.length) return
    const ids = alle.map((s) => s.id)
    const reihe = richtung < 0 ? ids : [...ids].reverse()
    for (let i = 1; i < reihe.length; i++) {
      if (auswahl.includes(reihe[i]) && !auswahl.includes(reihe[i - 1])) [reihe[i - 1], reihe[i]] = [reihe[i], reihe[i - 1]]
    }
    setHinweis(null)
    await ordne(richtung < 0 ? reihe : [...reihe].reverse())
  }

  /** Mit dem gegriffenen wandern alle markierten sichtbaren Schritte — ist er selbst markiert. */
  const mitGezogen = (key: string) => (markiert.has(key) ? zeilen.filter((z) => markiert.has(z.id)).map((z) => z.id) : [key])

  const { gezogen, ziel, dndProps } = useZeilenZiehen({
    behaelter,
    onAblegen: (key, vorKey) => {
      const ids = alle.map((s) => s.id)
      // Gefiltert: „ans Ende“ heißt hinter den letzten **sichtbaren** Schritt.
      let vor = vorKey
      if (vor === null && gefiltert && zeilen.length) vor = ids[ids.indexOf(zeilen[zeilen.length - 1].id) + 1] ?? null
      const neu = verschiebeMehrereVor(ids, mitGezogen(key), vor)
      if (neu !== ids) void ordne(neu)
    },
  })

  // ── Kopieren und Einfügen ───────────────────────────────────────────────

  function kopieren(ids: string[]) {
    if (!ids.length || !user?.id) return
    schreibeAblage({ userId: user.id, stepIds: ids, herkunft: `${projekt.projectNumber} / ${position.number}` })
    setFehler(null)
    setHinweis(`${ids.length} ${ids.length === 1 ? 'Schritt' : 'Schritte'} kopiert — in dieser oder einer anderen Position einfügen.`)
  }

  /** Vor dem ersten markierten Schritt, sonst am Ende. */
  async function einfuegen() {
    if (!ablage) return
    const vor = auswahl[0]
    await fuehreAus(
      async () => setDaten(await pasteWorkSteps(position.id, ablage.stepIds, vor)),
      `${ablage.stepIds.length} ${ablage.stepIds.length === 1 ? 'Schritt' : 'Schritte'} eingefügt — neu nummeriert, noch nicht freigegeben.`,
    )
  }

  const reihe = useMemo(() => zeilen.map((z) => z.id), [zeilen])
  const { amRand } = useRandMarkieren({ behaelter, reihe, markiert, setMarkiert, onKopieren: () => kopieren(auswahl) })

  // Strg+V fügt ein, solange kein Feld den Fokus hat — sonst bleibt es Text einfügen.
  const einfuegenRef = useRef(einfuegen)
  einfuegenRef.current = einfuegen
  useEffect(() => {
    if (!ablage || !darfPflegen) return
    const taste = (e: KeyboardEvent) => {
      if (e.defaultPrevented || !(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey || e.key.toLowerCase() !== 'v') return
      const fokus = document.activeElement
      if (fokus instanceof Element && fokus.closest('input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"]')) return
      // Nur, wenn dieser Reiter zu sehen ist.
      if (!behaelter.current?.getClientRects().length) return
      e.preventDefault()
      void einfuegenRef.current()
    }
    document.addEventListener('keydown', taste)
    return () => document.removeEventListener('keydown', taste)
  }, [ablage, darfPflegen])

  // ── Filter ──────────────────────────────────────────────────────────────

  const phasen = useMemo(() => daten?.phases ?? [], [daten])
  const filterFelder = useMemo<FilterFieldDef[]>(
    () => [
      { key: 'category', label: 'Kategorie', type: 'select-multi', category: 'core', operators: MEHRFACH, options: phasen.map((p) => ({ value: p.id, label: p.name })) },
      { key: 'dueUntil', label: 'Fertigstellung bis', type: 'date', category: 'core', operators: ['is'] },
      {
        key: 'status',
        label: 'Status',
        type: 'select-multi',
        category: 'core',
        operators: MEHRFACH,
        options: Object.entries(WORK_STEP_STATUS_LABELS).map(([value, l]) => ({ value, label: l.label })),
      },
      {
        key: 'released',
        label: 'Freigegeben',
        type: 'select',
        category: 'core',
        operators: ['is'],
        options: [
          { value: 'yes', label: 'ja' },
          { value: 'no', label: 'nein' },
        ],
      },
      {
        key: 'qualification',
        label: 'Qualifikation',
        type: 'select-multi',
        category: 'core',
        operators: MEHRFACH,
        options: [...qualifikationen.map((q) => ({ value: q.id, label: `${q.code} · ${q.name}` })), { value: OHNE, label: 'ohne Qualifikation' }],
      },
    ],
    [phasen, qualifikationen],
  )
  const anzahlFilter = filterAnzahl(filterWerte)

  /**
   * Nach einer Feldänderung die Liste nachholen: Soll gesamt, die Summe und die
   * Stunden je Kategorie rechnet der Server, die Antwort des PATCH trägt sie nicht.
   */
  async function neuLaden() {
    await laden()
    await onChanged()
  }

  const letztePhase = alle[alle.length - 1]?.phaseId ?? phasen[0]?.id ?? ''
  const felderGemeinsam = { phasen, qualifikationen, darfPflegen, breit }
  const gemeinsam = { projectId: projekt.id, ...felderGemeinsam, darfFreigeben }
  const auftrag = { projectNumber: projekt.projectNumber, positionNumber: position.number, darfAuftrag, darfSenden }
  const gezogeneZeile = gezogen ? alle.find((s) => s.id === gezogen) : undefined
  const gezogeneIds = new Set(gezogen ? mitGezogen(gezogen) : [])

  const spalten = useMemo<Spalte[]>(
    () =>
      breit
        ? // Nachtrag 15 (N15-18): Kategorie und Qualifikation als eigene Spalten nach dem Mengenbezug; die
          // Breiten sind die des abgenommenen Nachbaus, was übrig bleibt, bekommt die Bezeichnung.
          [
            { key: 'nr', label: 'Nr.', spur: '3rem', min: 48, schmal: 'w-14' },
            { key: 'soll', label: 'Soll Std. *', spur: '4rem', min: 64, schmal: 'w-20', rechts: true },
            { key: 'name', label: 'Bezeichnung *', spur: 'minmax(10rem,2fr)', min: 160, schmal: 'w-72' },
            { key: 'basis', label: 'Mengenbezug *', spur: '7rem', min: 112, schmal: 'w-36' },
            { key: 'kategorie', label: 'Kategorie *', spur: '8rem', min: 128, schmal: 'w-36' },
            { key: 'qualifikation', label: 'Qualifikation', spur: '9.5rem', min: 152, schmal: 'w-40' },
            { key: 'termin', label: 'Fertigstellung bis', spur: '7.25rem', min: 116, schmal: 'w-32' },
            { key: 'luecke', label: '', spur: '1.5rem', min: 24 },
            { key: 'fix', label: 'fixiert am', spur: '7.25rem', min: 116, schmal: 'w-32' },
            { key: 'uhrzeit', label: 'Uhrzeit', spur: '5.5rem', min: 88, schmal: 'w-24' },
          ]
        : [
            { key: 'nr', label: 'Nr.', spur: '3rem', min: 44, schmal: 'w-14' },
            { key: 'soll', label: 'Soll Std. *', spur: '4rem', min: 56, schmal: 'w-20', rechts: true },
            { key: 'name', label: 'Bezeichnung *', spur: 'minmax(8rem,2fr)', min: 128, schmal: 'w-72' },
            { key: 'basis', label: 'Mengenbezug *', spur: '7rem', min: 104, schmal: 'w-36' },
            { key: 'termin', label: 'Fertigstellung bis', spur: '7.25rem', min: 112, schmal: 'w-32' },
            // Abstand zwischen den Terminen und der Fixierung (Auftraggeber 19.9.2026).
            { key: 'luecke', label: '', spur: '1.5rem', min: 16 },
            { key: 'fix', label: 'fixiert am', spur: '7.25rem', min: 112, schmal: 'w-32' },
            { key: 'uhrzeit', label: 'Uhrzeit', spur: '5.5rem', min: 80, schmal: 'w-24' },
          ],
    [breit],
  )

  const freigebbar = alle.filter((s) => markiert.has(s.id) && ['PLANNED', 'PAUSED'].includes(s.executionStatus)).map((s) => s.id)

  return (
    <div ref={listenRef} className="space-y-2">
      {/* Bleibt eingehängt, auch eingeklappt: Sie stellt die zuletzt benutzten Filter wieder her, und der Knopf zählt sie. */}
      <div className={filterOffen ? '' : 'hidden'}>
        <FilterBar availableFields={filterFelder} value={filterWerte} onChange={setFilterWerte} layoutKey={FILTER_LAYOUT} defaultFieldKeys={FILTER_FELDER} festeFelder />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-expanded={filterOffen}
          aria-label={anzahlFilter ? `Filter — ${anzahlFilter} aktiv` : 'Filter'}
          title={filterOffen ? 'Filterleiste ausblenden' : 'Filterleiste einblenden'}
          className={`${knopf} ${anzahlFilter ? 'border-primary bg-primary/10 font-medium text-primary hover:bg-primary/15' : ''} ${filterOffen && !anzahlFilter ? 'bg-muted' : ''}`}
          onClick={() => schalteFilter(!filterOffen)}
        >
          <Filter size={14} className={anzahlFilter ? 'fill-current' : ''} /> Filter
          {anzahlFilter > 0 && <span className="rounded-full bg-primary px-1.5 text-[11px] leading-4 text-primary-foreground">{anzahlFilter}</span>}
          {filterOffen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        <KlappUmschalter modus={klappModus} onModus={setKlappModus} className="self-stretch rounded-md" />
        {darfPflegen && ablage && (
          <span className="inline-flex items-center overflow-hidden rounded-md border border-border text-sm" data-auswahl-behalten>
            <button
              type="button"
              className="inline-flex items-center gap-1 px-3 py-1.5 hover:bg-muted"
              title={auswahl.length ? 'vor dem ersten markierten Schritt einfügen (Strg+V)' : 'am Ende einfügen (Strg+V)'}
              onClick={() => void einfuegen()}
            >
              <ClipboardPaste size={14} /> Einfügen ({ablage.stepIds.length})
            </button>
            <button type="button" className="border-l border-border px-1.5 py-1.5 text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Ablage leeren" title={`kopiert aus ${ablage.herkunft} — Ablage leeren`} onClick={() => schreibeAblage(null)}>
              <X size={13} />
            </button>
          </span>
        )}

        {auswahl.length > 0 ? (
          <div className="ml-auto flex flex-wrap items-center gap-1 text-sm" data-auswahl-behalten>
            <span className="mr-1 text-muted-foreground">{auswahl.length} markiert</span>
            <button type="button" className={klein} onClick={() => kopieren(auswahl)}>
              <Copy size={13} /> Kopieren
            </button>
            {darfFreigeben && freigebbar.length > 0 && (
              <button type="button" className={klein} onClick={() => void freigeben(freigebbar)}>
                freigeben
              </button>
            )}
            {darfPflegen && (
              <>
                <button type="button" className={`${klein} p-1`} aria-label="nach oben" onClick={() => void schiebe(-1)}>
                  <ArrowUp size={14} />
                </button>
                <button type="button" className={`${klein} p-1`} aria-label="nach unten" onClick={() => void schiebe(1)}>
                  <ArrowDown size={14} />
                </button>
                <button type="button" className={`${klein} text-destructive`} onClick={() => void loeschen(auswahl)}>
                  <Trash2 size={13} /> löschen
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">
              Soll {alsStunden(daten?.summary.plannedHours ?? 0)} · {daten?.summary.count ?? 0} Schritte
              {phasen
                .filter((p) => p.plannedHours > 0)
                .map((p) => ` · ${p.name} ${alsStunden(p.plannedHours)}`)
                .join('')}
            </span>
            {darfPflegen && (
              <div className="relative">
                <button type="button" className={knopf} aria-expanded={papierkorbOffen} onClick={() => setPapierkorbOffen((x) => !x)}>
                  <Trash2 size={14} /> Papierkorb
                </button>
                {papierkorbOffen && (
                  <PapierkorbFenster
                    titel="Papierkorb der Arbeitsschritte"
                    laden={() => fetchWorkStepTrash(position.id)}
                    onWiederherstellen={(id) => void wiederherstellen(id)}
                    onSchliessen={() => setPapierkorbOffen(false)}
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {hinweis && <p className="text-sm text-green-700 dark:text-green-400">{hinweis}</p>}
      <RueckgaengigHinweis geloescht={geloescht} onRueckgaengig={(id) => void wiederherstellen(id)} onSchliessen={() => setGeloescht([])} />
      {gefiltert && (
        <p className="text-xs text-muted-foreground">
          {zeilen.length} von {alle.length} Schritten gefiltert{darfPflegen ? ' — neue Schritte erscheinen nur, wenn sie zum Filter passen.' : '.'}{' '}
          <button type="button" className="text-primary hover:underline" onClick={() => setFilterWerte({})}>
            Filter zurücksetzen
          </button>
        </p>
      )}
      {daten && phasen.length === 0 && <p className="text-sm text-muted-foreground">Es gibt noch keine Kategorien — lege sie in den Einstellungen unter „Phasen“ an.</p>}

      {daten && (
        <div ref={behaelter}>
          <ZiehBereich
            dndProps={dndProps}
            vorschau={
              gezogeneZeile && (
                <>
                  {gezogeneIds.size > 1 && <span className="mr-1 rounded bg-primary px-1.5 text-xs text-primary-foreground">{gezogeneIds.size} Schritte</span>}
                  <span className="font-mono text-xs text-muted-foreground">{gezogeneZeile.number}</span> <span className="font-medium">{gezogeneZeile.activityName}</span>
                </>
              )
            }
          >
            <ZeilenListe spalten={spalten} seite={breit ? SEITE_BREIT : SEITE} abgesetzt klappModus={klappModus}>
              {zeilen.length === 0 && !darfPflegen && <p className="px-3 py-6 text-center text-sm text-muted-foreground">{gefiltert ? 'Kein Schritt passt zum Filter.' : 'Noch keine Schritte.'}</p>}
              {zeilen.map((s) => (
                <SchrittZeile
                  key={s.id}
                  s={s}
                  {...gemeinsam}
                  markiert={markiert.has(s.id)}
                  ziehbar={darfPflegen}
                  gezogen={gezogeneIds.has(s.id)}
                  ziellinie={ziel?.key === s.id ? ziel.kante : null}
                  onRand={amRand(s.id)}
                  onNeuLaden={neuLaden}
                  onStatus={async (payload) => {
                    await changeWorkStepStatus(s.id, payload)
                    await neuLaden()
                  }}
                  onGeaendert={neuLaden}
                  onFreigeben={() => void freigeben([s.id])}
                  onAktion={(a) => void fuehreAus(a)}
                  onLoeschen={() => void loeschen([s.id])}
                  onDateien={darfDateien ? (d) => void dateienAnhaengen(s, d) : undefined}
                  auftrag={auftrag}
                />
              ))}
              {darfPflegen && phasen.length > 0 && (
                <NeueSchrittZeile
                  key={`neu-${neuSchluessel}`}
                  {...felderGemeinsam}
                  vorgabePhase={letztePhase}
                  vorgabeBezug={daten.quantityBasisDefault}
                  autoFokus={neuFokus}
                  onAnlegen={async (dto, perTastatur) => {
                    await createWorkStep(position.id, dto)
                    await neuLaden()
                    setNeuFokus(perTastatur)
                    setNeuSchluessel((k) => k + 1)
                  }}
                />
              )}
            </ZeilenListe>
          </ZiehBereich>
        </div>
      )}
    </div>
  )
}

/** Text ohne Rand — leer heißt `null`, wie es das Backend für „Feld räumen" erwartet. */
const leer = (s: string) => (s.trim() === '' ? null : s.trim())

/** Fertig melden geht erst nach der Freigabe; ein fertiger Schritt behält sein Datum änderbar. */
const fertigMoeglich = (s?: WorkStepRow) => Boolean(s && ['READY', 'ASSIGNED', 'IN_PROGRESS', 'DONE'].includes(s.executionStatus))

/**
 * Tab-Reihenfolge (Auftraggeber 19.9.2026, Nachtrag 10): Soll → Bezeichnung →
 * Beschreibung → Mengenbezug → Kategorie → Qualifikation → Fertigstellung bis →
 * nächster Schritt. Die übrigen Felder erreicht man mit der Maus oder aus
 * einem von ihnen weiter mit Tab.
 */
const KETTE = { soll: 1, name: 2, beschreibung: 3, basis: 4, kategorie: 5, qualifikation: 6, termin: 7 } as const

/** Pflichtfelder (Nachtrag 10): Soll, Bezeichnung, Kategorie, Mengenbezug — was davon fehlt. */
function pflichtFehlt(w: Entwurf): string[] {
  const soll = leseZahl(w.plannedHours)
  return [
    soll === null || soll < 0 ? 'Soll Std.' : null,
    w.activityName.trim() ? null : 'Bezeichnung',
    w.phaseId ? null : 'Kategorie',
    w.quantityBasis ? null : 'Mengenbezug',
  ].filter((x): x is string => x !== null)
}

// ── Die Anordnung der Felder ────────────────────────────────────────────────

/**
 * Die Anbindung eines Eingabefelds: bei einem bestehenden Schritt die Props aus
 * `useFeld` (Speicherbaustein), in der leeren letzten Zeile die des Entwurfs.
 * Beide Zeilenarten zeichnen damit **dieselbe** Anordnung — sonst driften die
 * Spalten der Zeile, die anlegt, von denen der Zeilen darüber ab.
 */
interface Bindung {
  value: string
  name?: string
  disabled?: boolean
  'aria-invalid'?: boolean
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void
  onBlur?: () => void
  onKeyDown?: (e: ReactKeyboardEvent<HTMLElement>) => void
  ref?: (el: HTMLElement | null) => void
}

type FeldName =
  | 'plannedHours'
  | 'activityName'
  | 'quantityBasis'
  | 'dueAt'
  | 'fixedAt'
  | 'fixedTime'
  | 'description'
  | 'phaseId'
  | 'qualificationId'
  | 'completedAt'
  | 'fixedNote'

type Bindungen = Record<FeldName, Bindung>

/** Ein Feld, das es in dieser Zeile nicht gibt — die Fertigmeldung der leeren Zeile. */
const KEIN_FELD: Bindung = { value: '', disabled: true, onChange: () => undefined }

interface Anordnung {
  f: Bindungen
  /** Der gespeicherte Schritt — fehlt in der leeren letzten Zeile. */
  s?: WorkStepRow
  phasen: WorkStepsResponse['phases']
  qualifikationen: ProjectQualification[]
  /**
   * Nachtrag 15 (N15-18): die breite Anordnung — Kategorie und Qualifikation in Zeile 1. Steht hier und nicht
   * an den Zeilen, damit die bestehende und die leere Zeile nie verschieden anordnen.
   */
  breit?: boolean
}

/** Die Auswahl der Kategorie — schmal unter dem Mengenbezug, breit in ihrer eigenen Spalte (dieselbe Bindung). */
function kategorieAuswahl({ f, phasen }: Anordnung) {
  return (
    <select className={eingabe} aria-label="Kategorie" aria-required data-haupt={KETTE.kategorie} {...f.phaseId}>
      {!f.phaseId.value && <option value="">—</option>}
      {phasen.map((p) => (
        <option key={p.id} value={p.id}>
          {p.name}
        </option>
      ))}
    </select>
  )
}

/** Die Auswahl der Qualifikation — wie die Kategorie an zwei Orten, je nach Anordnung. */
function qualifikationAuswahl({ f, qualifikationen }: Anordnung) {
  return (
    <select className={eingabe} aria-label="Qualifikation" data-haupt={KETTE.qualifikation} {...f.qualificationId}>
      <option value="">—</option>
      {qualifikationen.map((q) => (
        <option key={q.id} value={q.id}>
          {q.code} · {q.name}
        </option>
      ))}
    </select>
  )
}

/**
 * Zeile 1: Nr., Soll, Bezeichnung, Mengenbezug, Fertigstellung bis, Fixierung — breit dazwischen Kategorie und
 * Qualifikation (N15-18). Die Tab-Kette folgt `data-haupt`, nicht der Reihenfolge hier: Nach der Bezeichnung
 * geht es weiter in die Beschreibung der zweiten Zeile, dann zurück zum Mengenbezug.
 */
function zeileEins(a: Anordnung) {
  const { f, s, breit } = a
  const ueberfaellig = Boolean(s?.dueAt && new Date(s.dueAt) < new Date(new Date().toDateString()) && s.executionStatus !== 'DONE')
  return (
    <>
      <Feld spalte="nr" leer={!s}>
        <span className="block px-1 py-1 font-mono text-xs text-muted-foreground" title="Scan-Code auf dem Auftrag">
          {s?.number ?? ''}
        </span>
      </Feld>
      <Feld spalte="soll">
        <input
          data-start
          data-haupt={KETTE.soll}
          aria-required
          className={`${eingabe} text-right`}
          inputMode="decimal"
          placeholder={s ? '' : 'Std.'}
          {...f.plannedHours}
        />
      </Feld>
      <Feld spalte="name">
        <input
          data-haupt={KETTE.name}
          aria-required
          className={`${eingabe} font-medium`}
          placeholder={s ? '' : 'neuer Schritt — Bezeichnung eingeben'}
          {...f.activityName}
        />
      </Feld>
      <Feld spalte="basis">
        <select className={eingabe} aria-required data-haupt={KETTE.basis} {...f.quantityBasis}>
          <option value="TOTAL">gesamt</option>
          <option value="PER_UNIT">je Einheit</option>
        </select>
      </Feld>
      {breit && (
        <>
          <Feld spalte="kategorie">{kategorieAuswahl(a)}</Feld>
          <Feld spalte="qualifikation">{qualifikationAuswahl(a)}</Feld>
        </>
      )}
      <Feld spalte="termin">
        <input type="date" data-haupt={KETTE.termin} className={`${eingabe} ${ueberfaellig ? 'text-destructive' : ''}`} {...f.dueAt} />
      </Feld>
      <Feld spalte="luecke" leer>
        <span />
      </Feld>
      <Feld spalte="fix">
        <input type="date" className={eingabe} {...f.fixedAt} />
      </Feld>
      <Feld spalte="uhrzeit">
        <input type="time" className={eingabe} {...f.fixedTime} />
      </Feld>
    </>
  )
}

/**
 * Zeile 2, spaltengenau darunter: Beschreibung, Kategorie, Qualifikation, Fertigmeldung, Notiz. Breit (N15-18)
 * stehen Kategorie und Qualifikation schon in Zeile 1: Die Beschreibung reicht dann von der Bezeichnung bis zur
 * Qualifikation, und die beiden Textfelder sind nur noch zwei Zeilen hoch — unter ihnen steht nichts mehr, das
 * die Höhe ohnehin bräuchte.
 */
function zeileZwei(a: Anordnung) {
  const { f, s, breit } = a
  const hoehe = breit ? 2 : 4
  return (
    <>
      <RasterFeld von="name" bis={breit ? 'qualifikation' : undefined} label="Beschreibung" schmal="w-full max-w-xl">
        <textarea rows={hoehe} className={eingabe} data-haupt={KETTE.beschreibung} {...f.description} />
      </RasterFeld>
      {!breit && (
        <RasterFeld von="basis" label={<span>Kategorie *</span>} schmal="w-36">
          {kategorieAuswahl(a)}
          <span className="mt-1.5 block text-[11px] text-muted-foreground">Qualifikation</span>
          {qualifikationAuswahl(a)}
        </RasterFeld>
      )}
      <RasterFeld von="termin" label="Fertiggestellt am" schmal="w-36">
        <input
          type="date"
          className={eingabe}
          title={s && !fertigMoeglich(s) ? 'Fertig melden geht erst nach der Freigabe' : 'Datum eintragen meldet den Schritt fertig; leeren heißt Nacharbeit'}
          {...f.completedAt}
        />
      </RasterFeld>
      <RasterFeld von="fix" bis="uhrzeit" label="Notiz zur Fixierung" schmal="w-56">
        <textarea rows={hoehe} className={eingabe} {...f.fixedNote} />
      </RasterFeld>
    </>
  )
}

// ── Ein bestehender Schritt: eine Speicherstelle, die Zeile ihre Gruppe ─────

function SchrittZeile({
  s,
  projectId,
  phasen,
  qualifikationen,
  darfPflegen,
  breit,
  darfFreigeben,
  markiert,
  ziehbar,
  gezogen,
  ziellinie,
  onRand,
  onNeuLaden,
  onStatus,
  onGeaendert,
  onFreigeben,
  onAktion,
  onLoeschen,
  onDateien,
  auftrag,
}: {
  s: WorkStepRow
  projectId: string
  phasen: WorkStepsResponse['phases']
  qualifikationen: ProjectQualification[]
  darfPflegen: boolean
  /** Die Liste ist breit genug für die Anordnung aus Nachtrag 15 (N15-18). */
  breit: boolean
  darfFreigeben: boolean
  markiert?: boolean
  ziehbar?: boolean
  gezogen?: boolean
  ziellinie?: 'oben' | 'unten' | null
  onRand?: (e: ReactMouseEvent) => void
  /** Nach einer gespeicherten Änderung die Liste nachholen (Summen, Soll gesamt). */
  onNeuLaden: () => Promise<void>
  onStatus: (payload: { to: string; note?: string; completedAt?: string }) => Promise<void>
  onGeaendert?: () => Promise<void>
  onFreigeben?: () => void
  onAktion?: (a: () => Promise<unknown>) => void
  onLoeschen?: () => void
  onDateien?: (dateien: File[]) => void
  auftrag?: AuftragsAngaben
}) {
  /** Fehler der Handlungen dieser Zeile; Feldfehler zeigt die Gruppe selbst. */
  const [fehler, setFehler] = useState<string | null>(null)
  const gesperrt = !darfPflegen || s.executionStatus === 'CANCELLED'

  const stelle = useSpeicherstelle<Datensatz>({
    schluessel: `workstep:${s.id}`,
    bezeichnung: `Arbeitsschritt ${s.number} ${s.activityName}`,
    daten: s as unknown as Datensatz,
    schreibe: async (patch, stand) => {
      const res = await api.patch<{ data: Datensatz }>(`/projects/work-steps/${s.id}`, patch, mitStand(stand))
      void onNeuLaden()
      return res.data.data
    },
    keepalive: { url: `/projects/work-steps/${s.id}`, method: 'PATCH' },
  }) as Speicherstelle<unknown>
  const gruppe = useGruppe(stelle, { label: s.activityName })

  const soll = useFeld<number | null>(stelle, 'plannedHours', {
    gruppe,
    art: 'zahl',
    stellen: 2,
    pflicht: true,
    gesperrt,
    label: 'Soll Std.',
    pruefe: (w) => (w !== null && w < 0 ? 'Nicht kleiner als 0' : null),
  })
  const name = useFeld(stelle, 'activityName', { gruppe, pflicht: true, gesperrt, lies: leer, label: 'Bezeichnung' })
  const basis = useFeld(stelle, 'quantityBasis', { gruppe, art: 'auswahl', gesperrt, label: 'Mengenbezug' })
  const termin = useFeld(stelle, 'dueAt', { gruppe, art: 'datum', gesperrt, label: 'Fertigstellung bis' })
  // Fixiert heißt: ein Tag, optional eine Uhrzeit — ohne Tag geht die Uhrzeit im selben PATCH mit weg.
  const fixiert = useFeld(stelle, 'fixedAt', { gruppe, art: 'datum', gesperrt, label: 'fixiert am', folge: (w) => (w ? {} : { fixedTime: null }) })
  const uhrzeit = useFeld(stelle, 'fixedTime', { gruppe, gesperrt: gesperrt || !fixiert.wert, lies: leer, label: 'Uhrzeit' })
  const beschreibung = useFeld(stelle, 'description', { gruppe, gesperrt, lies: leer, label: 'Beschreibung' })
  const kategorie = useFeld(stelle, 'phaseId', { gruppe, art: 'auswahl', pflicht: true, gesperrt, label: 'Kategorie' })
  const qualifikation = useFeld(stelle, 'qualificationId', { gruppe, art: 'auswahl', gesperrt, label: 'Qualifikation' })
  const fixNotiz = useFeld(stelle, 'fixedNote', { gruppe, gesperrt: gesperrt || !fixiert.wert, lies: leer, label: 'Notiz zur Fixierung' })
  const wartetAuf = useFeld(stelle, 'releaseNote', { gruppe, gesperrt: !darfPflegen, lies: leer, label: 'wartet auf' })

  /**
   * „Fertiggestellt am" ist kein gewöhnliches Feld: Eintragen meldet fertig,
   * Leeren heißt Nacharbeit — beides sind Statuswechsel und keine Feldänderung
   * (der PATCH lehnt sie ab). Nur das Umdatieren eines fertigen Schritts geht
   * über die Stelle. Deshalb liegt der Wert hier und entscheidet beim Verlassen.
   */
  const fertigGespeichert = s.completedAt?.slice(0, 10) ?? ''
  const [fertigAm, setFertigAm] = useState(fertigGespeichert)
  useEffect(() => setFertigAm(fertigGespeichert), [fertigGespeichert])

  async function fertigVerlassen() {
    if (gesperrt || fertigAm === fertigGespeichert) return
    setFehler(null)
    try {
      if (!fertigGespeichert && fertigAm && ['READY', 'ASSIGNED', 'IN_PROGRESS'].includes(s.executionStatus)) {
        await onStatus({ to: 'DONE', completedAt: fertigAm })
        return
      }
      if (fertigGespeichert && !fertigAm && s.executionStatus === 'DONE') {
        const grund = await frage('Begründung für die Nacharbeit')
        if (!grund?.trim()) return setFertigAm(fertigGespeichert)
        await onStatus({ to: 'IN_PROGRESS', note: grund.trim() })
        return
      }
      if (s.executionStatus === 'DONE' && fertigAm) {
        const ergebnis = await stelle.sende({
          kennung: 'completedAt',
          felder: ['completedAt'],
          labels: { completedAt: 'Fertiggestellt am' },
          patch: { completedAt: fertigAm },
        })
        if (ergebnis.art === 'fehler') setFehler(ergebnis.text)
        else if (ergebnis.art === 'verworfen') setFertigAm(fertigGespeichert)
        return
      }
      setFertigAm(fertigGespeichert)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'))
    }
  }

  const f: Bindungen = {
    plannedHours: soll.props,
    activityName: name.props,
    quantityBasis: basis.props,
    dueAt: termin.props,
    fixedAt: fixiert.props,
    fixedTime: uhrzeit.props,
    description: beschreibung.props,
    phaseId: kategorie.props,
    qualificationId: qualifikation.props,
    fixedNote: fixNotiz.props,
    completedAt: {
      value: fertigAm,
      disabled: gesperrt || !fertigMoeglich(s) || undefined,
      onChange: (e) => setFertigAm(e.target.value),
    },
  }
  const anordnung: Anordnung = { f, s, phasen, qualifikationen, breit }

  return (
    <ZeilenBlock
      gruppe={gruppe}
      gedimmt={s.executionStatus === 'CANCELLED'}
      schluessel={s.id}
      gezogen={gezogen}
      ziellinie={ziellinie}
      ziehbar={ziehbar}
      markiert={markiert}
      onRand={onRand}
      randLabel={`${s.number} ${s.activityName}`}
      onDateien={onDateien}
      tabKette
      onVerlassen={() => void fertigVerlassen()}
      onVerwerfen={() => {
        setFertigAm(fertigGespeichert)
        setFehler(null)
      }}
      fehler={fehler}
      zeilen={[zeileZwei(anordnung)]}
      seite={() => (
        <StatusBlock
          s={s}
          projectId={projectId}
          wartetAuf={wartetAuf.props}
          breit={breit}
          darfPflegen={darfPflegen}
          darfFreigeben={darfFreigeben}
          onFreigeben={onFreigeben}
          onAktion={onAktion}
          onLoeschen={onLoeschen}
          onGeaendert={onGeaendert}
          auftrag={auftrag}
        />
      )}
      hinweis={s.quantityBasis === 'PER_UNIT' ? <span>Soll gesamt {alsStunden(s.plannedHoursTotal)}</span> : undefined}
    >
      {zeileEins(anordnung)}
    </ZeilenBlock>
  )
}

// ── Die leere letzte Zeile: sie legt an (Fall 2) ────────────────────────────

interface Entwurf {
  phaseId: string
  activityName: string
  plannedHours: string
  quantityBasis: 'TOTAL' | 'PER_UNIT'
  qualificationId: string
  dueAt: string
  description: string
  fixedAt: string
  fixedTime: string
  fixedNote: string
}

const entwurfLeer = (phase: string, bezug: 'TOTAL' | 'PER_UNIT' = 'TOTAL'): Entwurf => ({
  phaseId: phase,
  activityName: '',
  plannedHours: '',
  quantityBasis: bezug,
  qualificationId: '',
  dueAt: '',
  description: '',
  fixedAt: '',
  fixedTime: '',
  fixedNote: '',
})

/**
 * Sie hat keine Speicherstelle, weil es den Datensatz noch nicht gibt: Der
 * Entwurf steht lokal, und beim Verlassen entsteht der Schritt in einem Stück.
 * Danach baut die Liste sie neu auf, und es gilt Fall 1.
 */
function NeueSchrittZeile({
  phasen,
  qualifikationen,
  darfPflegen,
  breit,
  vorgabePhase,
  vorgabeBezug = 'TOTAL',
  autoFokus,
  onAnlegen,
}: {
  phasen: WorkStepsResponse['phases']
  qualifikationen: ProjectQualification[]
  darfPflegen: boolean
  breit: boolean
  vorgabePhase: string
  /** Mengenbezug der leeren Zeile (Einstellungen). */
  vorgabeBezug?: 'TOTAL' | 'PER_UNIT'
  autoFokus?: boolean
  onAnlegen: (dto: Record<string, unknown>, perTastatur: boolean) => Promise<void>
}) {
  const [w, setW] = useState<Entwurf>(() => entwurfLeer(vorgabePhase, vorgabeBezug))
  const [fehler, setFehler] = useState<string | null>(null)
  /** Die Pflichtfelder sind rot erst nach dem ersten Versuch — nicht schon in der leeren Zeile. */
  const [geprueft, setGeprueft] = useState(false)
  const fehlt = geprueft ? pflichtFehlt(w) : []

  const binde = (key: keyof Entwurf, pflichtName?: string): Bindung => ({
    value: w[key],
    disabled: !darfPflegen || undefined,
    'aria-invalid': pflichtName && fehlt.includes(pflichtName) ? true : undefined,
    onChange: (e) => {
      const wert = e.target.value
      setW((x) => ({ ...x, [key]: wert }))
    },
  })

  const f: Bindungen = {
    plannedHours: binde('plannedHours', 'Soll Std.'),
    activityName: binde('activityName', 'Bezeichnung'),
    quantityBasis: binde('quantityBasis', 'Mengenbezug'),
    dueAt: binde('dueAt'),
    fixedAt: {
      ...binde('fixedAt'),
      // Ohne Tag keine Uhrzeit — sonst bliebe eine Zeit stehen, die zu nichts gehört.
      onChange: (e) => {
        const wert = e.target.value
        setW((x) => ({ ...x, fixedAt: wert, fixedTime: wert ? x.fixedTime : '' }))
      },
    },
    fixedTime: { ...binde('fixedTime'), disabled: !darfPflegen || !w.fixedAt || undefined },
    description: binde('description'),
    phaseId: binde('phaseId', 'Kategorie'),
    qualificationId: binde('qualificationId'),
    fixedNote: { ...binde('fixedNote'), disabled: !darfPflegen || !w.fixedAt || undefined },
    completedAt: KEIN_FELD,
  }

  async function anlegen(perTastatur: boolean) {
    if (!darfPflegen) return
    // Nichts eingegeben heißt nichts anlegen, und zwar ohne Meldung.
    if (JSON.stringify(w) === JSON.stringify(entwurfLeer(vorgabePhase, vorgabeBezug))) return
    const fehlend = pflichtFehlt(w)
    if (fehlend.length) {
      setGeprueft(true)
      setFehler(`Pflichtfelder fehlen: ${fehlend.join(', ')}`)
      return
    }
    const dto: Record<string, unknown> = {
      activityName: w.activityName.trim(),
      phaseId: w.phaseId,
      plannedHours: leseZahl(w.plannedHours),
      quantityBasis: w.quantityBasis,
    }
    // Beim Anlegen kennt der Server kein `null` — leere Felder fallen weg.
    if (w.qualificationId) dto.qualificationId = w.qualificationId
    if (w.dueAt) dto.dueAt = w.dueAt
    if (leer(w.description)) dto.description = leer(w.description)
    if (w.fixedAt) {
      dto.fixedAt = w.fixedAt
      if (w.fixedTime) dto.fixedTime = w.fixedTime
    }
    if (leer(w.fixedNote)) dto.fixedNote = leer(w.fixedNote)
    try {
      setFehler(null)
      setGeprueft(false)
      await onAnlegen(dto, perTastatur)
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Schritt ließ sich nicht speichern.'))
    }
  }

  return (
    <ZeilenBlock
      neu
      autoFokus={autoFokus}
      tabKette
      onVerlassen={(t) => void anlegen(t)}
      onVerwerfen={() => {
        setW(entwurfLeer(vorgabePhase, vorgabeBezug))
        setFehler(null)
        setGeprueft(false)
      }}
      fehler={fehler}
      zeilen={[zeileZwei({ f, phasen, qualifikationen, breit })]}
      seite={() => null}
    >
      {zeileEins({ f, phasen, qualifikationen, breit })}
    </ZeilenBlock>
  )
}

// ── Block rechts: Status, Freigaben, Dateien ────────────────────────────────

interface AuftragsAngaben {
  projectNumber: string
  positionNumber: number
  darfAuftrag: boolean
  darfSenden: boolean
}

function StatusBlock({
  s,
  projectId,
  wartetAuf,
  breit,
  darfPflegen,
  darfFreigeben,
  onFreigeben,
  onAktion,
  onLoeschen,
  onGeaendert,
  auftrag,
}: {
  s: WorkStepRow
  projectId: string
  /** „wartet auf" gehört zur Gruppe der Zeile — der Block zeichnet es nur. */
  wartetAuf: Bindung
  /** Breite Anordnung der Liste (N15-18): Der Block ist doppelt so breit und ordnet in zwei Spalten. */
  breit: boolean
  darfPflegen: boolean
  darfFreigeben: boolean
  onFreigeben?: () => void
  onAktion?: (a: () => Promise<unknown>) => void
  onLoeschen?: () => void
  onGeaendert?: () => Promise<void>
  auftrag?: AuftragsAngaben
}) {
  const [dateienOffen, setDateienOffen] = useState(false)
  const badge = WORK_STEP_STATUS_LABELS[s.executionStatus]
  const freigegeben = (SCHRITT_FREIGEGEBEN as readonly string[]).includes(s.executionStatus)
  // Freigeben aus „nicht freigegeben“ oder „pausiert“, widerrufen aus „freigegeben“ oder „in Arbeit“.
  const schaltbar = darfFreigeben && ['PLANNED', 'PAUSED', 'READY', 'ASSIGNED', 'IN_PROGRESS'].includes(s.executionStatus)
  const ueberSoll = s.actualHours > s.plannedHoursTotal && s.plannedHoursTotal > 0
  const dateiText = `${s.fileCount} ${s.fileCount === 1 ? 'Datei' : 'Dateien'}`
  // Zwei Spalten nur, solange der Block **neben** den Feldern steht — darunter liegt er als eine Zeile wie bisher.
  const daneben = useSeiteDaneben()
  const zweispaltig = breit && daneben

  async function umschalten(an: boolean) {
    if (an) return onFreigeben?.()
    const grund = await frage('Begründung für den Widerruf')
    if (grund?.trim()) onAktion?.(() => revokeWorkStepRelease(s.id, grund.trim()))
  }

  // Die Teile des Blocks — einmal gebaut, je nach Anordnung untereinander oder in zwei Spalten gesetzt.
  const status = (
    <>
      <div className="flex items-start justify-between gap-1">
        <span className={`inline-flex rounded-full px-2 py-0.5 font-medium ${badge?.class ?? ''}`}>{badge?.label ?? s.executionStatus}</span>
        {darfPflegen && onLoeschen && (
          // Ohne Rückfrage — „Rückgängig“ und der Papierkorb holen den Schritt zurück (Nachtrag 10).
          <button type="button" className="shrink-0 p-0.5 text-muted-foreground hover:text-destructive" aria-label="löschen" title="in den Papierkorb" onClick={onLoeschen}>
            <Trash2 size={14} />
          </button>
        )}
      </div>
      <span className={`mt-0.5 block text-[11px] tabular-nums ${ueberSoll ? 'text-destructive' : 'text-muted-foreground'}`} title="aus der Zeiterfassung">
        Ist {s.actualHours > 0 ? alsStunden(s.actualHours) : '—'}
      </span>
    </>
  )
  const freigabe = (
    <>
      <div className="text-[11px] font-semibold text-muted-foreground">Freigaben</div>
      <label className="mt-1 flex items-start gap-1.5">
        <input type="checkbox" className="mt-0.5" checked={freigegeben} disabled={!schaltbar} onChange={(e) => void umschalten(e.target.checked)} />
        <span>
          {freigegeben ? (s.release?.byName ?? 'freigegeben') : 'Ausführung'}
          {freigegeben && s.release && <span className="block text-[11px] text-muted-foreground">{alsTag(s.release.at)}</span>}
        </span>
      </label>
    </>
  )
  const wartet = (abstand: string) =>
    !freigegeben && (
      <label className={`${abstand} block`}>
        <span className="block text-[11px] text-muted-foreground">wartet auf</span>
        <input className={eingabe} {...wartetAuf} />
      </label>
    )
  // Nachtrag 9: der Auftrag — erst nach der Freigabe; Drucken oder Senden teilt zu.
  const auftragTeil = (abstand: string) =>
    auftrag?.darfAuftrag &&
    (PROJECT_WORK_STEP_STATUSES_RELEASED as readonly string[]).includes(s.executionStatus) && (
      <div className={abstand}>
        <div className="text-[11px] font-semibold text-muted-foreground">Auftrag</div>
        <div className="mt-1">
          <WorkOrderButton
            kompakt
            projectId={projectId}
            projectNumber={auftrag.projectNumber}
            positionNumber={auftrag.positionNumber}
            step={{ id: s.id, number: s.number }}
            darfSenden={auftrag.darfSenden}
            onDone={() => void onGeaendert?.()}
          />
        </div>
      </div>
    )
  const dateien = (
    <div>
      <div className="text-[11px] font-semibold text-muted-foreground">Dateien</div>
      <button type="button" className="mt-1 inline-flex items-center gap-1 rounded border border-border px-2 py-0.5 hover:bg-muted" onClick={() => setDateienOffen((x) => !x)}>
        <Paperclip size={12} /> {s.fileCount > 0 ? dateiText : 'hinzufügen'}
      </button>
    </div>
  )

  return (
    <>
      {zweispaltig ? (
        // Nachtrag 15 (N15-18): links, was der Schritt ist (Status, Ist, Freigabe), rechts, was an ihm zu tun
        // ist („wartet auf" bzw. der Auftrag, Dateien) — dazwischen eine gestrichelte Linie. So ist der Block
        // nur noch halb so hoch und bestimmt die Höhe der Zeile nicht mehr.
        <div className="grid grid-cols-2 gap-x-3.5">
          <div className="min-w-0">
            {status}
            <div className="mt-1.5">{freigabe}</div>
          </div>
          <div className="flex min-w-0 flex-col gap-1.5 border-l border-dashed border-border pl-3">
            {wartet('')}
            {auftragTeil('')}
            {dateien}
          </div>
        </div>
      ) : (
        <>
          <div className="min-w-[7.5rem]">{status}</div>
          <div className="min-w-[8rem]">
            {freigabe}
            {wartet('mt-1.5')}
          </div>
          {auftragTeil('mb-1.5')}
          {dateien}
        </>
      )}
      {dateienOffen && (
        <Aufklapper titel={`Dateien · ${s.activityName}`} breit onSchliessen={() => setDateienOffen(false)}>
          <ProjectFilesPanel projectId={projectId} entityType="WORK_STEP" entityId={s.id} onChanged={onGeaendert} />
        </Aufklapper>
      )}
    </>
  )
}
